import { Formik, Form } from "formik";
import { Button, Box, Flex } from "@chakra-ui/react";
import { useContext, useState } from "react";
import { AppContext } from "AppProvider/AppContext";
import ApprovalsFormForeign from "./ApprovalsFormForeign";
import WorkFlowDescForeign from "./WorkFlowDescForeign";
import {
  generateApprovalsArray,
  generateLevels,
  transformResponse,
} from "./utils/function";
import { formValidation } from "./utils/validation";
import { imageToBase64 } from "utils/functions/function";
import debounce from "lodash/debounce";
import { appRequest } from "routes";
import { foreignJsonData } from "./utils/foreignjsonData";

const WorkFlowFormForeign = ({
  showError,
  loading,
  setShowError,
  submitForm,
  initialVal,
  onClose,
  type,
  setDeletedApproavals,
  activePage,
  setActiveSteps,
  setDisabledSteps,
  disabledSteps,
  steps,
  workflowProcess
}: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { toastNotify } = useContext(AppContext);

  const handleGetUser = async (currentP?: number, valu?: any) => {
    try {
      setIsLoading(true);
      let limit = 20;
      let sort = 1;
      let page = currentP ? currentP : 1;

      let dt: any = {
        limit,
        sort,
        page,
      };

      if (valu) {
        dt["value"] = valu;
      }
      const response: any = await appRequest("user", "get", dt);
      if (response) {
        if (Array.isArray(response.data)) {
          return response.data;
        } else {
          return [];
        }
      } else {
        toastNotify({
          title: `Error getting the user.`,
          message: response.message || "Something went wrong.",
          type: "error",
        });
      }
    } catch (error: any) {
      toastNotify({
        title: `Error getting the user.`,
        description: error.message || "Something went wrong.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedLoadOptions = debounce(
    async (inputValue: string, callback: (options: any[]) => void) => {
      try {
        setIsLoading(true);
        let dt = await handleGetUser(1, inputValue);
        setIsLoading(false);
        if (dt.length === 0) {
          dt = [{ email: inputValue, id: inputValue, isText: 1 }];
        }
        callback(dt);
      } catch (error) {
        setIsLoading(false);
        callback([]);
      }
    },
    800
  );

  const loadOptions = (
    inputValue: string,
    callback: (options: any[]) => void
  ) => {
    debouncedLoadOptions(inputValue, callback);
  };

  const handleLogoChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: any
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        imageToBase64(file)
          .then((base64String: string) => {
            setFieldValue("logo", { name: file.name, file: base64String });
          })
          .catch((error: any) => {
            console.error("Error converting image to base64:", error);
          });
      } else {
        alert("please upload image");
      }
    }
  };

  return (
    <Box>
      <Formik
        initialValues={initialVal}
        enableReinitialize
        onSubmit={(values, { resetForm }) => {
          if (steps && activePage < steps.length - 1) {
            setDisabledSteps((prev: any) => [...prev, activePage + 1]);
            setActiveSteps(activePage + 1);
            return;
          }
          const transformedValues = transformResponse(values);
          const finalSubmissionData = Array.isArray(transformedValues)
            ? transformedValues.map((val: any) => ({ ...val, ...foreignJsonData }))
            : { ...transformedValues, ...foreignJsonData };
          console.log("Foreign Workflow Submission Data:", finalSubmissionData);

          if (steps && activePage === steps.length - 1) {
            submitForm(finalSubmissionData, resetForm);
          }
        }}
        validationSchema={formValidation}
      >
        {({ values, errors, handleChange, setFieldValue }) => {
          return (
            <Form>
              <Box
                overflowX="auto"
                className="customScrollBar"
                maxH={{ base: "60vh", lg: "72vH" }}
                p={2}
              >
                {/* Workflow description (Workflow Features) */}
                <WorkFlowDescForeign
                  activePage={activePage}
                  showError={showError}
                  errors={errors}
                  values={values}
                  handleChange={handleChange}
                  isDisabled={false}
                  setFieldValue={setFieldValue}
                  handleLogoChange={handleLogoChange}
                  isLoading={isLoading}
                  loadOptions={loadOptions}
                  type={type}
                />
                {/* User Approvals Form (Users Section) */}
                <ApprovalsFormForeign
                  activePage={activePage}
                  values={values}
                  setFieldValue={setFieldValue}
                  isDisabled={false}
                  setDeletedApproavals={setDeletedApproavals}
                  type={type}
                  data={[]}
                  showError={showError}
                  generateLevels={generateLevels}
                  isLoading={isLoading}
                  loadOptions={loadOptions}
                  debouncedLoadOptions={debouncedLoadOptions}
                />
              </Box>
              <Flex
                gap={2}
                position="absolute"
                bottom={0}
                right={0}
                bg="white"
                p={2}
                zIndex={1}
              >
                <Button
                  type="button"
                  colorScheme="red"
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                    setActiveSteps(0);
                    setDisabledSteps([0]);
                  }}
                  w={{ base: "4rem", md: "5rem" }}
                >
                  Cancel
                </Button>
                {activePage > 0 && (
                  <Button
                    type="button"
                    bg="teal.300"
                    color="white"
                    _hover={{ bg: "teal.400" }}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveSteps(activePage - 1);
                    }}
                    w={{ base: "4rem", md: "5rem" }}
                  >
                    Back
                  </Button>
                )}
                {steps && activePage < steps.length - 1 ? (
                  <Button
                    type="button"
                    bg="teal.300"
                    color="white"
                    _hover={{ bg: "teal.400" }}
                    onClick={(e) => {
                      e.preventDefault();
                      setDisabledSteps((prev: any) => [...prev, activePage + 1]);
                      setActiveSteps(activePage + 1);
                    }}
                    isDisabled={
                      activePage === 0 &&
                      (values.workFlowName === "" || !values.workFlowName)
                    }
                    w={{ base: "4rem", md: "5rem" }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    isLoading={loading}
                    bg="green.400"
                    color="white"
                    _hover={{ bg: "green.500" }}
                    onClick={() => {
                      setShowError(true);
                    }}
                    w={{ base: "4rem", md: "5rem" }}
                  >
                    Save
                  </Button>
                )}
              </Flex>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default WorkFlowFormForeign;
